import { groupBy, uniq } from 'es-toolkit';
import type { ExchangeIssuedItem } from '@/components/iem/workflows/exchange/types';

const normalizeValue = (value?: string | null, fallback = '—') => {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : fallback;
};

export interface IssuedItemGroupingOptions {
  splitByReceiptType?: boolean;
}

export const getIssuedItemGroupKey = (
  item: ExchangeIssuedItem,
  options: IssuedItemGroupingOptions = {},
) => {
  const lin = normalizeValue(item.lin, '—');
  const nsn = normalizeValue(item.nsn, '—');
  const name = normalizeValue(item.name, 'Unknown Item');
  const segments = [lin, nsn, name];

  if (options.splitByReceiptType) {
    segments.push(item.receiptType ?? '—');
  }

  return segments.join('::');
};

export const getDefaultReplacementQty = (item: ExchangeIssuedItem) => {
  const issuedQty = Math.max(0, item.issuedQty ?? 0);
  return issuedQty > 0 ? Math.min(1, issuedQty) : 0;
};

export interface IssuedItemGroup {
  id: string;
  lin: string;
  nsn: string;
  name: string;
  /** Receipt types present in this group (PHR, THR, or both) */
  receiptTypes: Set<NonNullable<ExchangeIssuedItem['receiptType']>>;
  issuedDates: string[];
  totalIssuedQty: number;
  items: ExchangeIssuedItem[];
}

export const groupExchangeIssuedItems = (
  items: ExchangeIssuedItem[],
  options: IssuedItemGroupingOptions = {},
): IssuedItemGroup[] => {
  if (items.length === 0) {
    return [];
  }

  const grouped = groupBy(items, (item) => getIssuedItemGroupKey(item, options));

  return Object.entries(grouped).map(([groupId, groupItems]) => {
    const first = groupItems[0]!;
    const receiptTypes = new Set<NonNullable<ExchangeIssuedItem['receiptType']>>();
    let totalIssuedQty = 0;
    const issuedDates: string[] = [];

    for (const item of groupItems) {
      totalIssuedQty += Math.max(0, item.issuedQty ?? 0);
      if (item.receiptType) {
        receiptTypes.add(item.receiptType);
      }
      const issuedAt = normalizeValue(item.issuedAt, '—');
      if (!issuedDates.includes(issuedAt)) {
        issuedDates.push(issuedAt);
      }
    }

    return {
      id: groupId,
      lin: normalizeValue(first.lin, '—'),
      nsn: normalizeValue(first.nsn, '—'),
      name: normalizeValue(first.name, 'Unknown Item'),
      receiptTypes,
      issuedDates,
      totalIssuedQty,
      items: groupItems,
    };
  });
};

export const calculateGroupReplacementQty = (
  group: IssuedItemGroup,
  replacementQtyByIssuedId: Record<string, number>,
) => {
  return group.items.reduce((sum, item) => {
    const stored = replacementQtyByIssuedId[item.id];
    const fallback = getDefaultReplacementQty(item);
    return sum + (typeof stored === 'number' ? stored : fallback);
  }, 0);
};

export const formatIssuedDatesDisplay = (dates: string[]): string => {
  if (!dates.length) {
    return '—';
  }

  const normalizedDates = uniq(dates.map((date) => normalizeValue(date, '—')));

  if (normalizedDates.length === 0) {
    return '—';
  }

  return normalizedDates.length === 1 ? (normalizedDates[0] ?? '—') : 'Multiple';
};
